import type { CampaignResults } from '../shared/campaigns';
import { currentArchiveRecords } from '../shared/archive-history';
import './archive-history.css';

const stamp = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value.split('-').reverse().join('/')
    : new Date(value).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        dateStyle: 'short',
        timeStyle: 'short',
      });

export function ArchiveHistoryWorkspace({
  results,
  titleFor,
}: {
  results: CampaignResults;
  titleFor: (id: string) => string;
}) {
  const media = results.media.filter(
    (item) => item.archiveReadings?.length || item.archivePublicationEvidence?.length,
  );
  if (!media.length) return null;
  return (
    <section className="cw-section archive-history" aria-labelledby="archive-history-title">
      <div className="cw-section-heading">
        <div>
          <h2 id="archive-history-title">Histórico importado do acervo</h2>
          <p>
            Registros documentais com fonte e revisão. As leituras não comprovam separação entre
            orgânico e pago nem uma janela D+7 exata. Não entram nos totais das leituras da API
            Meta.
          </p>
        </div>
        <a
          className="button"
          href={`/api/campaigns/${encodeURIComponent(results.campaignId)}/results.csv`}
        >
          Exportar CSV com histórico
        </a>
      </div>
      {media.map((item) => {
        const readings = item.archiveReadings ?? [],
          proofs = item.archivePublicationEvidence ?? [];
        const currentReadings = new Set(
          currentArchiveRecords(readings).map((reading) => reading.id),
        );
        const currentProofs = new Set(currentArchiveRecords(proofs).map((proof) => proof.id));
        return (
          <article className="archive-publication" key={item.id}>
            <h3>{titleFor(item.contentId)}</h3>
            <p>
              {item.account} · {item.platform}
            </p>
            {proofs.map((proof) => (
              <details key={proof.id}>
                <summary>
                  Publicação documentada · {stamp(proof.publishedAt || proof.date)} · revisão{' '}
                  {proof.revision}
                  {currentProofs.has(proof.id) ? '' : ' · anterior'}
                </summary>
                <p>{proof.timeNote}</p>
                {proof.conflicts.map((conflict) => (
                  <p className="archive-warning" key={conflict}>
                    {conflict}
                  </p>
                ))}
                <pre>{proof.evidence}</pre>
                <p className="archive-reference">
                  {proof.sourceRef.path}:{proof.line}
                  <br />
                  SHA-256: {proof.sourceRef.hash}
                </p>
              </details>
            ))}
            {readings.map((reading) => (
              <details key={reading.id}>
                <summary>
                  Leitura de {stamp(reading.collectedAt)} ·{' '}
                  {reading.coverage === 'partial' ? 'parcial' : 'conforme a fonte'}
                  {currentReadings.has(reading.id) ? '' : ' · corrigida, preservada'}
                </summary>
                <p>
                  <strong>{reading.sourceLabel}</strong> · São Paulo · horário informado até o
                  minuto
                </p>
                <p>{reading.windowNote}</p>
                <dl className="archive-metrics">
                  {reading.metrics.map((metric) => (
                    <div key={metric.key}>
                      <dt>{metric.label}</dt>
                      <dd>
                        {metric.status === 'available' && metric.value !== null
                          ? metric.value.toLocaleString('pt-BR')
                          : '— · não mostrado pela fonte'}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p>{reading.note}</p>
                <p className="archive-reference">
                  {reading.sourceRef.path}:{reading.line}
                  <br />
                  SHA-256: {reading.sourceRef.hash} · revisão {reading.revision}
                </p>
                <details>
                  <summary>Linha original da fonte</summary>
                  <pre>{reading.evidence}</pre>
                </details>
              </details>
            ))}
          </article>
        );
      })}
    </section>
  );
}
